import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import FramedAvatar from '../components/FramedAvatar';
import { toast } from 'sonner';
import {
  Trophy,
  ArrowLeft,
  Medal,
  Crown,
  Star,
  TrendingUp,
  MessageSquare,
  Zap
} from 'lucide-react';

const LeaderboardPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leaderboardRes, badgesRes] = await Promise.all([
        api.get('/leaderboard?limit=20'),
        api.get('/badges')
      ]);
      setLeaderboard(leaderboardRes.data);
      // Convert badges dict to array with id field
      const badgesData = badgesRes.data;
      const badgesArray = Object.entries(badgesData).map(([id, badge]) => ({ id, ...badge }));
      setBadges(badgesArray);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

  const getRankIcon = (index) => {
    if (index === 0) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (index === 1) return <Medal className="w-6 h-6 text-gray-400" />;
    if (index === 2) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-muted-foreground font-bold">{index + 1}</span>;
  };

  const getRoleBadgeClass = (role) => {
    const classes = { admin: 'role-admin', major: 'role-major', ambassadeur: 'role-ambassadeur', odys_mentoring: 'role-odys_mentoring', odys_start: 'role-odys_start' };
    return classes[role] || '';
  };

  const getRoleLabel = (role) => {
    const labels = { admin: 'Admin', major: 'Major', ambassadeur: 'Ambassadeur', odys_mentoring: 'Mentoring', odys_start: 'Start' };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Trophy className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="leaderboard-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center gold-glow">
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl">Classement</h1>
              <p className="text-sm text-muted-foreground">Top membres de la communauté</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Podium */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {leaderboard.slice(0, 3).map((user, index) => {
            const positions = [1, 0, 2]; // Reorder for podium display
            const actualIndex = positions[index];
            const member = leaderboard[actualIndex];
            if (!member) return null;
            
            return (
              <Card key={member.id} className={`card-base text-center cursor-pointer hover-lift ${actualIndex === 0 ? 'scale-105 gold-glow-strong' : ''}`} onClick={() => navigate(`/members/${member.id}`)}>
                <CardContent className="pt-6">
                  <div className="mb-3">{getRankIcon(actualIndex)}</div>
                  <div className="flex justify-center mb-3">
                    <FramedAvatar
                      src={member.avatar_url}
                      fallback={getInitials(member.first_name, member.last_name)}
                      frame={member.profile_frame}
                      role={member.role}
                      seniority={member.seniority}
                      size="xl"
                    />
                  </div>
                  <h3 className="font-semibold">{member.first_name} {member.last_name}</h3>
                  <Badge className={`mt-1 ${getRoleBadgeClass(member.role)}`}>{getRoleLabel(member.role)}</Badge>
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center justify-center gap-1 text-primary">
                      <Zap className="w-4 h-4" />
                      <span className="font-bold">{member.points} pts</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 text-muted-foreground text-sm">
                      <TrendingUp className="w-3 h-3" />
                      <span>Niveau {member.level}</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 text-muted-foreground text-sm">
                      <MessageSquare className="w-3 h-3" />
                      <span>{member.messages_count} messages</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Full Leaderboard */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading">Tous les membres</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {leaderboard.map((member, index) => (
                  <div key={member.id} onClick={() => navigate(`/members/${member.id}`)} className={`flex items-center gap-4 p-3 rounded-lg hover:bg-secondary/30 transition-colors cursor-pointer ${index < 3 ? 'bg-primary/5' : ''}`} data-testid={`leaderboard-${member.id}`}>
                    <div className="w-8">{getRankIcon(index)}</div>
                    <FramedAvatar
                      src={member.avatar_url}
                      fallback={getInitials(member.first_name, member.last_name)}
                      frame={member.profile_frame}
                      role={member.role}
                      seniority={member.seniority}
                      size="lg"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{member.first_name} {member.last_name}</span>
                        <Badge className={`text-xs ${getRoleBadgeClass(member.role)}`}>{getRoleLabel(member.role)}</Badge>
                        <span className="level-badge">Niv.{member.level}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{member.messages_count} messages</span>
                        {member.badges?.length > 0 && (
                          <div className="flex gap-1">
                            {member.badges.slice(0, 3).map(badge => {
                              const badgeInfo = badges.find(b => b.id === badge);
                              return badgeInfo ? <span key={badge} title={badgeInfo.name}>{badgeInfo.icon}</span> : null;
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-primary">{member.points} pts</div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Badges */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Star className="w-5 h-5 text-primary" />
              Badges disponibles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {badges.map(badge => (
                <div key={badge.id} className="p-4 bg-secondary/30 rounded-lg text-center hover:bg-secondary/50 transition-colors">
                  <div className="text-3xl mb-2">{badge.icon}</div>
                  <h4 className="font-semibold">{badge.name}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                  <p className="text-xs text-primary mt-2">{badge.requirement}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LeaderboardPage;
